import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createOperationStudent } from '../../services/operationStudentService';
import { FileSpreadsheet, Download, Search, Trash2, Circle, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import * as XLSX from 'xlsx';

interface IMATStudentListProps {
    programType: string;
    universityList: string[];
}

interface Student {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    status: string;
    notes: string;
    is_eu: boolean;
    universitaly_data: {
        email?: string;
        password?: string;
        program?: string;
        final_choice?: string; // Kesin Tercih
        payment_status?: string; // Odeme
        first_choice?: string; // Ilk Tercih
        registration_status?: string; // Kaydi
        post_status?: string; // Post
        description?: string; // Aciklama (if different from notes)
    };
    created_at: string;
    type: 'operation' | 'regular';
    leads?: any[];
}

// Custom StackedSelects Component for EU students (Multi-choice)
const StackedSelects: React.FC<{
    options: string[];
    selected: string[];
    onChange: (values: string[]) => void;
}> = ({ options, selected, onChange }) => {
    const handleUpdate = (index: number, value: string) => {
        const newSelected = [...selected];
        if (value === "") {
            newSelected.splice(index, 1);
        } else {
            newSelected[index] = value;
        }
        onChange(newSelected);
    };

    const handleAdd = (value: string) => {
        if (value && !selected.includes(value)) {
            onChange([...selected, value]);
        }
    };

    return (
        <div className="flex flex-col gap-1 w-full py-1">
            {selected.map((item, index) => (
                <div key={`${item}-${index}`} className="flex items-center gap-1 group">
                    <select
                        className="flex-1 h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                        value={item}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdate(index, e.target.value)}
                    >
                        <option value="">SİL (Kaldır)</option>
                        {options.map(uni => (
                            <option key={uni} value={uni}>{uni}</option>
                        ))}
                    </select>
                </div>
            ))}
            <select
                className="w-full h-8 px-2 bg-blue-50/30 dark:bg-blue-900/10 border border-dashed border-blue-200 dark:border-blue-800 rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs text-blue-600 dark:text-blue-400 font-medium"
                value=""
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleAdd(e.target.value)}
            >
                <option value="">+ TERCİH EKLE...</option>
                {options.map(uni => (
                    <option key={uni} value={uni} disabled={selected.includes(uni)}>{uni}</option>
                ))}
            </select>
        </div>
    );
};

const IMATStudentList: React.FC<IMATStudentListProps> = ({ programType, universityList }) => {
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState<Student[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { user } = useAuth();

    // Add Student State
    const initialNewStudent = {
        first_name: '',
        last_name: '',
        is_eu: false,
        universitaly_data: {
            program: '',
            final_choice: '',
            payment_status: '',
            email: '',
            password: '',
            description: '',
            first_choice: '',
            registration_status: '',
            post_status: ''
        }
    };
    const [newStudent, setNewStudent] = useState(initialNewStudent);
    const [isAdding, setIsAdding] = useState(false);

    // Column Resizing Logic
    const defaultWidths: Record<string, number> = {
        first_name: 120,
        last_name: 120,
        is_eu: 60,
        program: 150,
        final_choice: 200,
        payment_status: 100,
        email: 180,
        password: 120,
        description: 200,
        first_choice: 150,
        registration_status: 120,
        post_status: 100,
        actions: 60
    };

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        const saved = localStorage.getItem('imat_column_widths');
        return saved ? JSON.parse(saved) : defaultWidths;
    });

    const handleResize = (column: string, width: number) => {
        const newWidths = { ...columnWidths, [column]: Math.max(width, 50) };
        setColumnWidths(newWidths);
        localStorage.setItem('imat_column_widths', JSON.stringify(newWidths));
    };

    const startResizing = (e: React.MouseEvent, column: string) => {
        e.preventDefault();
        const startX = e.pageX;
        const startWidth = columnWidths[column];

        const onMouseMove = (moveEvent: MouseEvent) => {
            const currentWidth = startWidth + (moveEvent.pageX - startX);
            handleResize(column, currentWidth);
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'default';
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        document.body.style.cursor = 'col-resize';
    };

    useEffect(() => {
        fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programType]);

    const getPageTitle = () => {
        const titles: Record<string, string> = {
            'imat': 'Tüm IMAT Öğrencileri',
            'sapienza': 'La Sapienza Öğrencileri',
            'milano-statale': 'Milano Statale Öğrencileri',
            'pavia': 'Pavia Öğrencileri',
            'bologna': 'Bologna Öğrencileri',
            'padova': 'Padova Öğrencileri',
            'tor-vergata': 'Tor Vergata Öğrencileri',
            'turin': 'Turin (Torino) Öğrencileri',
            'bicocca': 'Milano Bicocca Öğrencileri',
            'federico': 'Federico II Öğrencileri',
            'parma': 'Parma Öğrencileri',
            'messina': 'Messina Öğrencileri',
            'vanvitelli': 'Luigi Vanvitelli Öğrencileri',
            'bari': 'Bari Öğrencileri',
            'catania': 'Catania Öğrencileri',
            'marche': 'Marche (Ancona) Öğrencileri',
            'cagliari': 'Cagliari Öğrencileri',
            'siena-dentistry': 'Siena (Diş Hekimliği) Öğrencileri',
            'sapienza-dentistry': 'La Sapienza (Diş Hekimliği) Öğrencileri',
            'bologna-vetmed': 'Bologna (Veterinerlik) Öğrencileri',
        };
        return titles[programType] || 'Öğrenci Listesi';
    };

    const fetchStudents = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('operation_students')
                .select('*')
                .order('created_at', { ascending: false });

            // Apply branch filtering for non-admins
            if (user?.role && !['admin', 'accounting'].includes(user.role.toLowerCase())) {
                query = query.eq('branch_id', user.branchId);
            }

            const { data: opStudents, error: opError } = await query;

            if (opError) throw opError;

            const filtered = (opStudents || []).filter(student => {
                let keywords: string[] = [];
                switch (programType) {
                    case 'imat': keywords = ['imat']; break; // Only IMAT
                    case 'sapienza': keywords = ['sapienza', 'rome', 'roma']; break;
                    case 'milano-statale': keywords = ['statale', 'milano', 'milan']; break;
                    case 'pavia': keywords = ['pavia']; break;
                    case 'bologna': keywords = ['bologna']; break;
                    case 'padova': keywords = ['padova', 'padua']; break;
                    case 'tor-vergata': keywords = ['tor vergata', 'vergata']; break;
                    case 'turin': keywords = ['turin', 'torino']; break;
                    case 'bicocca': keywords = ['bicocca']; break;
                    case 'federico': keywords = ['federico', 'naples', 'napoli']; break;
                    case 'parma': keywords = ['parma']; break;
                    case 'messina': keywords = ['messina']; break;
                    case 'vanvitelli': keywords = ['vanvitelli', 'campania']; break;
                    case 'bari': keywords = ['bari']; break;
                    case 'catania': keywords = ['catania']; break;
                    case 'marche': keywords = ['marche', 'ancona']; break;
                    case 'cagliari': keywords = ['cagliari']; break;
                    case 'siena-dentistry': keywords = ['siena', 'dentistry', 'diş']; break;
                    case 'sapienza-dentistry': keywords = ['sapienza', 'dentistry', 'diş']; break;
                    case 'bologna-vetmed': keywords = ['bologna', 'vet', 'veterinary', 'veteriner']; break;
                    default: keywords = [];
                }

                if (keywords.length === 0) return true;

                const notesLower = (student.notes || '').toLowerCase();
                const progLower = (student.universitaly_data?.program || '').toLowerCase();
                const finalChoiceLower = (student.universitaly_data?.final_choice || '').toLowerCase();

                // If it's medicine and imat is in notes, it should NOT show up (already handled by IMAT keyword)
                if (programType === 'medicine' && notesLower.includes('imat')) return false;

                return keywords.some(k =>
                    notesLower.includes(k) ||
                    progLower.includes(k) ||
                    finalChoiceLower.includes(k)
                );
            });

            setStudents(filtered.map(s => ({
                ...s,
                type: 'operation',
                universitaly_data: s.universitaly_data || {},
                is_eu: s.is_eu || false
            })));

        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    // Auto-save logic
    const saveTimeouts = React.useRef<Record<string, any>>({});

    const debouncedSave = async (student: Student) => {
        if (saveTimeouts.current[student.id]) {
            clearTimeout(saveTimeouts.current[student.id]);
        }

        saveTimeouts.current[student.id] = setTimeout(async () => {
            try {
                await supabase
                    .from('operation_students')
                    .update({
                        first_name: student.first_name,
                        last_name: student.last_name,
                        universitaly_data: student.universitaly_data,
                        is_eu: student.is_eu
                    })
                    .eq('id', student.id);
            } catch (error) {
                console.error('Auto-save error:', error);
            }
            delete saveTimeouts.current[student.id];
        }, 500);
    };

    const updateStudentState = (id: string, field: string, value: any, nestedField?: string) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s };
            if (nestedField) {
                // @ts-ignore
                updated[nestedField] = { ...updated[nestedField], [field]: value };
            } else {
                // @ts-ignore
                updated[field] = value;
            }
            debouncedSave(updated as Student);
            return updated as Student;
        }));
    };


    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('operation_students')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setStudents(prev => prev.filter(s => s.id !== id));
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Silinirken hata oluştu');
        }
    };

    const handleAddStudent = async () => {
        if (!newStudent.first_name.trim() || !newStudent.last_name.trim() || isAdding) return;

        // Capture current focus to restore it on the new row
        const activeElement = document.activeElement;
        const focusedField = activeElement?.getAttribute('data-field');

        try {
            setIsAdding(true);
            const snapshot = { ...newStudent };

            let uniName = 'IMAT';
            let progName = 'Medicine';
            let formattedProgramType = programType;

            if (programType !== 'imat') {
                uniName = programType.charAt(0).toUpperCase() + programType.slice(1).replace('-', ' ');
                if (uniName.includes('Dentistry')) progName = 'Dentistry';
                if (uniName.includes('Vet')) progName = 'Veterinary';
            }

            const { data, error: rpcError } = await createOperationStudent({
                p_first_name: snapshot.first_name,
                p_last_name: snapshot.last_name,
                p_branch_id: user?.branchId,
                p_program_type: formattedProgramType,
                p_university_name: uniName,
                p_program_name: progName,
                p_created_by: user?.id,
                p_is_eu: snapshot.is_eu,
                p_universitaly_data: snapshot.universitaly_data
            });

            if (rpcError) throw rpcError;
            if (data && !data.success) throw new Error(data.error);

            const operationId = data.operation_id;

            if (operationId) {
                // Optimistic Update: Add to local state immediately
                const newRecord: Student = {
                    id: operationId,
                    first_name: snapshot.first_name,
                    last_name: snapshot.last_name,
                    email: '', phone: '', status: '', notes: '',
                    is_eu: snapshot.is_eu,
                    universitaly_data: snapshot.universitaly_data,
                    created_at: new Date().toISOString(),
                    type: 'operation'
                };

                setStudents(prev => [newRecord, ...prev]);
                setNewStudent(initialNewStudent);

                // Focus Shifting: Move focus to the same field in the newly created row
                if (focusedField) {
                    setTimeout(() => {
                        const selector = `[data-student-id="${operationId}"][data-field="${focusedField}"]`;
                        const nextInput = document.querySelector(selector) as HTMLElement;
                        if (nextInput) nextInput.focus();
                    }, 100);
                }
            }

        } catch (error: any) {
            console.error('Error adding student:', error);
            alert('Kayıt oluşturulamadı. Lütfen teknik ekibe bildirin veya SQL güncellenmediyse walkthrough.md dosyasındaki adımları takip edin.\n\nHata: ' + (error.message || 'Bilinmeyen hata'));
            setIsAdding(false); // Ensure isAdding is reset on error
        } finally {
            setIsAdding(false);
        }
    };

    // Auto-create trigger removed per user request. 
    // Records are now created only via "Kaydet" button or Enter key.

    const handleExportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(students.map(s => ({
            'ADI': s.first_name,
            'SOYADI': s.last_name,
            'DURUM': s.is_eu ? 'AB (EU)' : 'NON-EU',
            'PROGRAM': s.universitaly_data?.program || '',
            'KESİN TERCİH': s.universitaly_data?.final_choice || '',
            'ODEME': s.universitaly_data?.payment_status || '',
            'KULLANICI MAIL': s.universitaly_data?.email || '',
            'UNIVERSITALY ŞİFRE': s.universitaly_data?.password || '',
            'ACIKLAMA': s.universitaly_data?.description || '',
            'İLK TERCİH': s.universitaly_data?.first_choice || '',
            'KAYDI': s.universitaly_data?.registration_status || '',
            'POST': s.universitaly_data?.post_status || ''
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "IMAT Students");
        XLSX.writeFile(wb, `${programType}_students.xlsx`);
    };

    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.universitaly_data?.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                        {getPageTitle()}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Toplam {filteredStudents.length} öğrenci listeleniyor
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Ara..."
                            className="pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={handleExportExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Excel İndir
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse" style={{ tableLayout: 'fixed' }}>
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.first_name }}>
                                    <span className="truncate block">ADI</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'first_name')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.last_name }}>
                                    <span className="truncate block">SOYADI</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'last_name')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group text-center" style={{ width: columnWidths.is_eu }}>
                                    <span className="truncate block">EU</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'is_eu')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.program }}>
                                    <span className="truncate block">PROGRAM</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'program')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.final_choice }}>
                                    <span className="truncate block">KESİN TERCİH</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'final_choice')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.payment_status }}>
                                    <span className="truncate block">ODEME</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'payment_status')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group bg-yellow-100/50 dark:bg-yellow-900/20" style={{ width: columnWidths.email }}>
                                    <span className="truncate block">KULLANICI MAIL</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'email')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group bg-yellow-100/50 dark:bg-yellow-900/20" style={{ width: columnWidths.password }}>
                                    <span className="truncate block">UNIVERSITALY ŞİFRE</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'password')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.description }}>
                                    <span className="truncate block">ACIKLAMA</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'description')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.first_choice }}>
                                    <span className="truncate block">İLK TERCİH</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'first_choice')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group" style={{ width: columnWidths.registration_status }}>
                                    <span className="truncate block">KAYDI</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'registration_status')} />
                                </th>
                                <th className="px-4 py-3 border-b border-r dark:border-gray-600 relative group bg-orange-100/50 dark:bg-orange-900/20" style={{ width: columnWidths.post_status }}>
                                    <span className="truncate block">POST</span>
                                    <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10" onMouseDown={(e) => startResizing(e, 'post_status')} />
                                </th>
                                <th className="px-4 py-3 border-b dark:border-gray-600 text-center relative group" style={{ width: columnWidths.actions }}>
                                    <span className="truncate block">İŞLEM</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr><td colSpan={13} className="p-8 text-center text-gray-500">Yükleniyor...</td></tr>
                            ) : (
                                <>
                                    {/* Add New Row at TOP - ALWAYS VISIBLE */}
                                    <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b-2 border-blue-200 dark:border-blue-800">
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.first_name }}>
                                            <input
                                                id="new-student-first-name"
                                                data-field="first_name"
                                                placeholder="Ad"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.first_name}
                                                onChange={e => setNewStudent({ ...newStudent, first_name: e.target.value })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.last_name }}>
                                            <input
                                                data-field="last_name"
                                                placeholder="Soyad"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.last_name}
                                                onChange={e => setNewStudent({ ...newStudent, last_name: e.target.value })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 text-center" style={{ width: columnWidths.is_eu }}>
                                            <button
                                                data-field="is_eu"
                                                onClick={() => setNewStudent({ ...newStudent, is_eu: !newStudent.is_eu })}
                                                className={`p-2 rounded-full transition-colors ${newStudent.is_eu ? 'text-blue-600 bg-blue-100' : 'text-gray-400 bg-gray-100'}`}
                                            >
                                                {newStudent.is_eu ? <Globe size={18} /> : <Circle size={18} />}
                                            </button>
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.program }}>
                                            <input
                                                data-field="program"
                                                placeholder="Program"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.program}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, program: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 align-top overflow-hidden" style={{ width: columnWidths.final_choice }}>
                                            {newStudent.is_eu ? (
                                                <StackedSelects
                                                    options={universityList}
                                                    selected={newStudent.universitaly_data.final_choice ? newStudent.universitaly_data.final_choice.split(', ').filter(Boolean) : []}
                                                    onChange={(values) => setNewStudent({
                                                        ...newStudent,
                                                        universitaly_data: { ...newStudent.universitaly_data, final_choice: values.join(', ') }
                                                    })}
                                                />
                                            ) : (
                                                <select
                                                    data-field="final_choice"
                                                    className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                    value={newStudent.universitaly_data.final_choice}
                                                    onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, final_choice: e.target.value } })}
                                                >
                                                    <option value="">Seçiniz...</option>
                                                    {universityList.map(uni => (
                                                        <option key={uni} value={uni}>{uni}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.payment_status }}>
                                            <input
                                                data-field="payment_status"
                                                placeholder="Ödeme"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.payment_status}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, payment_status: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/10 overflow-hidden" style={{ width: columnWidths.email }}>
                                            <input
                                                data-field="email"
                                                placeholder="Mail"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.email}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, email: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/10 overflow-hidden" style={{ width: columnWidths.password }}>
                                            <input
                                                data-field="password"
                                                placeholder="Şifre"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.password}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, password: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.description }}>
                                            <input
                                                data-field="description"
                                                placeholder="Açıklama"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.description}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, description: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.first_choice }}>
                                            <input
                                                data-field="first_choice"
                                                placeholder="İlk Tercih"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.first_choice}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, first_choice: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 overflow-hidden" style={{ width: columnWidths.registration_status }}>
                                            <input
                                                data-field="registration_status"
                                                placeholder="Kaydı"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.registration_status}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, registration_status: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="border-r dark:border-gray-700 bg-orange-50 dark:bg-orange-900/10 overflow-hidden" style={{ width: columnWidths.post_status }}>
                                            <input
                                                data-field="post_status"
                                                placeholder="Post"
                                                className="w-full text-xs p-2 border rounded bg-white dark:bg-gray-800 outline-none focus:ring-1 focus:ring-blue-500"
                                                value={newStudent.universitaly_data.post_status}
                                                onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, post_status: e.target.value } })}
                                                onKeyDown={e => e.key === 'Enter' && handleAddStudent()}
                                            />
                                        </td>
                                        <td className="p-2 text-center" style={{ width: columnWidths.actions }}>
                                            {isAdding ? (
                                                <div className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <button
                                                    onClick={handleAddStudent}
                                                    disabled={!newStudent.first_name.trim() || !newStudent.last_name.trim()}
                                                    className="p-1 px-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-[10px] font-bold transition-colors uppercase"
                                                    title="Kaydet"
                                                >
                                                    Kaydet
                                                </button>
                                            )}
                                        </td>
                                    </tr>

                                    {filteredStudents.length === 0 ? (
                                        <tr><td colSpan={13} className="p-8 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
                                    ) : (
                                        filteredStudents.map((student) => {
                                            return (
                                                <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group">
                                                    <td className="border-r dark:border-gray-700 p-0 overflow-hidden" style={{ width: columnWidths.first_name }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="first_name"
                                                            className="w-full h-full px-2 py-3 bg-transparent border-transparent hover:border-gray-300 focus:border-blue-500 rounded outline-none font-medium uppercase"
                                                            value={student.first_name}
                                                            onChange={e => updateStudentState(student.id, 'first_name', e.target.value)}
                                                        />
                                                    </td>
                                                    <td className="border-r dark:border-gray-700 p-0 overflow-hidden" style={{ width: columnWidths.last_name }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="last_name"
                                                            className="w-full h-10 px-2 bg-transparent border-transparent hover:border-gray-300 focus:border-blue-500 rounded outline-none font-medium uppercase text-xs"
                                                            value={student.last_name}
                                                            onChange={e => updateStudentState(student.id, 'last_name', e.target.value)}
                                                        />
                                                    </td>

                                                    {/* EU Toggle */}
                                                    <td className="border-r dark:border-gray-700 text-center" style={{ width: columnWidths.is_eu }}>
                                                        <button
                                                            data-student-id={student.id}
                                                            data-field="is_eu"
                                                            onClick={() => updateStudentState(student.id, 'is_eu', !student.is_eu)}
                                                            className={`p-1 rounded-full transition-colors ${student.is_eu ? 'text-blue-600 bg-blue-100' : 'text-gray-400 bg-gray-100'}`}
                                                        >
                                                            {student.is_eu ? <Globe size={18} /> : <Circle size={18} />}
                                                        </button>
                                                    </td>

                                                    {/* Editable Fields */}
                                                    <td className="border-r dark:border-gray-700 p-1 overflow-hidden" style={{ width: columnWidths.program }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="program"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.program || ''}
                                                            onChange={e => updateStudentState(student.id, 'program', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 align-top overflow-hidden" style={{ width: columnWidths.final_choice }}>
                                                        {student.is_eu ? (
                                                            <StackedSelects
                                                                options={universityList}
                                                                selected={(student.universitaly_data?.final_choice || '').split(', ').filter(Boolean)}
                                                                onChange={(values) => updateStudentState(student.id, 'final_choice', values.join(', '), 'universitaly_data')}
                                                            />
                                                        ) : (
                                                            <select
                                                                data-student-id={student.id}
                                                                data-field="final_choice"
                                                                className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                                value={student.universitaly_data?.final_choice || ''}
                                                                onChange={e => updateStudentState(student.id, 'final_choice', e.target.value, 'universitaly_data')}
                                                            >
                                                                <option value="">Seçiniz...</option>
                                                                {universityList.map(uni => (
                                                                    <option key={uni} value={uni}>{uni}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 overflow-hidden" style={{ width: columnWidths.payment_status }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="payment_status"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.payment_status || ''}
                                                            onChange={e => updateStudentState(student.id, 'payment_status', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 bg-yellow-50 dark:bg-yellow-900/10 overflow-hidden" style={{ width: columnWidths.email }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="email"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.email || ''}
                                                            onChange={e => updateStudentState(student.id, 'email', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 bg-yellow-50 dark:bg-yellow-900/10 overflow-hidden" style={{ width: columnWidths.password }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="password"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.password || ''}
                                                            onChange={e => updateStudentState(student.id, 'password', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 overflow-hidden" style={{ width: columnWidths.description }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="description"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.description || ''}
                                                            onChange={e => updateStudentState(student.id, 'description', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 overflow-hidden" style={{ width: columnWidths.first_choice }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="first_choice"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.first_choice || ''}
                                                            onChange={e => updateStudentState(student.id, 'first_choice', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 overflow-hidden" style={{ width: columnWidths.registration_status }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="registration_status"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.registration_status || ''}
                                                            onChange={e => updateStudentState(student.id, 'registration_status', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="border-r dark:border-gray-700 p-1 bg-orange-50 dark:bg-orange-900/10 overflow-hidden" style={{ width: columnWidths.post_status }}>
                                                        <input
                                                            type="text"
                                                            data-student-id={student.id}
                                                            data-field="post_status"
                                                            className="w-full h-8 px-2 bg-white dark:bg-gray-800 border rounded focus:ring-1 focus:ring-blue-500 outline-none text-xs"
                                                            value={student.universitaly_data?.post_status || ''}
                                                            onChange={e => updateStudentState(student.id, 'post_status', e.target.value, 'universitaly_data')}
                                                        />
                                                    </td>

                                                    <td className="px-4 py-2 text-center text-gray-400" style={{ width: columnWidths.actions }}>
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => handleDelete(student.id)}
                                                                className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                                                title="Sil"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}

                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default IMATStudentList;
